<template>
  <div class="flex flex-col gap-4">
    <!-- Loading state -->
    <div v-if="isLoadingSessions" class="flex justify-center py-4">
      <LoadingIndicator :loading="isLoadingSessions" />
    </div>

    <!-- No sessions -->
    <div
      v-else-if="sessions.length === 0"
      class="text-gray-500 text-center py-4"
    >
      No sessions found for this user
    </div>

    <!-- Sessions list -->
    <template v-else>
      <div
        v-for="session in sessions"
        :key="session.sessionId"
        class="border rounded-lg overflow-hidden"
      >
        <!-- Session header -->
        <div
          class="flex items-center justify-between p-4 bg-gray-50 cursor-pointer"
          @click="toggleSession(session.sessionId)"
        >
          <div class="flex flex-col">
            <div class="font-medium">
              Session {{ session.sessionId.split("-")[1] }}
            </div>
            <div class="text-sm text-gray-500">
              {{ new Date(session.timestamp).toLocaleString() }}
            </div>
          </div>
          <button
            class="p-2 hover:bg-gray-200 rounded-full transition-colors"
            :class="{ 'rotate-180': selectedSessionId === session.sessionId }"
          >
            ▼
          </button>
        </div>

        <!-- Session events -->
        <div
          v-show="selectedSessionId === session.sessionId"
          class="p-4 bg-gray-100"
        >
          <SessionEvents :events="events" :is-loading="isLoadingEvents" />
        </div>
      </div>

      <!-- Load more button -->
      <button
        v-if="hasMoreSessions"
        @click="$emit('load-more')"
        class="mt-2 w-full py-2 px-4 text-center text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
        :disabled="isLoadingSessions"
      >
        {{ isLoadingSessions ? "Loading..." : "Load more sessions" }}
      </button>
    </template>
  </div>
</template>

<script setup>
import { computed } from "vue";
import LoadingIndicator from "@/components/LoadingIndicator.vue";
import SessionEvents from "./SessionEvents.vue";
import { useAnalytics } from "@/stores";

const analyticsStore = useAnalytics();

const props = defineProps({
  sessions: {
    type: Array,
    required: true,
  },
  isLoadingSessions: {
    type: Boolean,
    default: false,
  },
  hasMoreSessions: {
    type: Boolean,
    default: false,
  },
});

defineEmits(["load-more"]);

// Computed properties from store
const selectedSessionId = computed(() => analyticsStore.selectedSessionId);
const events = computed(() => analyticsStore.events);
const isLoadingEvents = computed(() => analyticsStore.isLoadingEvents);

// Methods
const toggleSession = async (sessionId) => {
  if (selectedSessionId.value === sessionId) {
    await analyticsStore.selectSession(null);
  } else {
    await analyticsStore.selectSession(sessionId);
  }
};
</script>
